import { RoleAccessGate } from '@components/common';
import { SettingsDashboardPage } from '@components/settings/dashboard/SettingsDashboardPage';
import { EbmConfigurationPage } from '@components/settings/ebm/EbmConfigurationPage';
import { EbmSaleRecordsPage } from '@components/settings/ebm/EbmSaleRecordsPage';
import { MenuItemsConfiguredFiscalSetupPage } from '@components/settings/ebm/MenuItemsConfiguredFiscalSetupPage';
import { MenuItemsMissingFiscalSetupPage } from '@components/settings/ebm/MenuItemsMissingFiscalSetupPage';
import { EmailConfigurationsPage } from '@components/settings/emailConfig/EmailConfigurationsPage';
import {
  AllPrintersPage,
  PrinterConfigurationPage,
} from '@components/settings/printers';
import {
  AccessControlsPage,
  PoliciesPage,
  RegisteredDevicesPage,
  RoleAssignmentsPage,
  RolesPermissionsPage,
  SecurityAccessDenied,
  SecurityPage,
} from '@components/settings/security';
import { UsersPage } from '@components/settings/users';
import { useAppSelector } from '@hooks/useRedux';
import Billing from '@pages/Billing';
import { AccessControlCode } from '@shared/constants';
import { UserRole } from '@shared/types';
import { Navigate, Route } from 'react-router-dom';
import { SettingsPage } from '../pages/SettingsPage';
import { selectHasFiscalIntegration } from '../store/auth/authSelectors';
import { AccessControlledDashboardPage } from './featureRouteHelpers';

export const useSettingsRoutes = () => {
  const hasFiscalIntegration = useAppSelector(selectHasFiscalIntegration);

  return (
    <Route path="/settings" element={<SettingsPage />}>
      <Route index element={<Navigate to="/settings/dashboard" replace />} />

      <Route
        path="dashboard"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.SETTINGS_PAGE_ACCESS}
            title="Settings dashboard access restricted"
            description="Your account does not currently have permission to access this page."
          >
            <SettingsDashboardPage />
          </AccessControlledDashboardPage>
        }
      />

      <Route
        path="users"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.SETTINGS_USERS_MANAGE}
            title="User accounts access restricted"
            description="Your account does not currently have permission to manage employee accounts."
          >
            <UsersPage />
          </AccessControlledDashboardPage>
        }
      />

      <Route
        path="roles"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.SETTINGS_ROLES_MANAGE}
            title="Role management access restricted"
            description="Your account does not currently have permission to manage roles and policy assignments."
          >
            <RolesPermissionsPage />
          </AccessControlledDashboardPage>
        }
      />

      <Route
        path="security"
        element={
          <RoleAccessGate
            allowedRoles={[UserRole.SUPER, UserRole.SUPER_ADMIN]}
            fallback={<SecurityAccessDenied />}
          >
            <SecurityPage />
          </RoleAccessGate>
        }
      >
        <Route
          index
          element={<Navigate to="/settings/security/access-controls" replace />}
        />
        <Route
          path="access-controls"
          element={
            <AccessControlledDashboardPage
              control={AccessControlCode.SETTINGS_SECURITY_ACCESS_CONTROLS_VIEW}
              title="Access controls restricted"
              description="Your account does not currently have permission to view the access control catalog."
            >
              <AccessControlsPage />
            </AccessControlledDashboardPage>
          }
        />
        <Route
          path="policies"
          element={
            <AccessControlledDashboardPage
              control={AccessControlCode.SETTINGS_SECURITY_POLICIES_MANAGE}
              title="Policy management restricted"
              description="Your account does not currently have permission to manage access policies."
            >
              <PoliciesPage />
            </AccessControlledDashboardPage>
          }
        />
        <Route
          path="assignments"
          element={
            <AccessControlledDashboardPage
              control={
                AccessControlCode.SETTINGS_SECURITY_ROLE_ASSIGNMENTS_MANAGE
              }
              title="Role assignments restricted"
              description="Your account does not currently have permission to manage role policy assignments."
            >
              <RoleAssignmentsPage />
            </AccessControlledDashboardPage>
          }
        />
        <Route
          path="registered-devices"
          element={
            <AccessControlledDashboardPage
              control={
                AccessControlCode.SETTINGS_SECURITY_REGISTERED_DEVICES_MANAGE
              }
              title="Registered devices restricted"
              description="Your account does not currently have permission to manage registered devices."
            >
              <RegisteredDevicesPage />
            </AccessControlledDashboardPage>
          }
        />
      </Route>

      <Route
        path="email-configurations"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.SETTINGS_PAGE_ACCESS}
            title="Email configurations access restricted"
            description="Your account does not currently have permission to access this page."
          >
            <EmailConfigurationsPage />
          </AccessControlledDashboardPage>
        }
      />

      <Route path="printers">
        <Route
          index
          element={<Navigate to="/settings/printers/all" replace />}
        />
        <Route
          path="all"
          element={
            <AccessControlledDashboardPage
              control={AccessControlCode.DASHBOARD_SECTIONS_TABLES_MANAGE}
              title="Printer settings restricted"
              description="Your account does not currently have permission to manage printers."
            >
              <AllPrintersPage />
            </AccessControlledDashboardPage>
          }
        />
        <Route
          path="configuration"
          element={
            <AccessControlledDashboardPage
              control={AccessControlCode.DASHBOARD_SECTIONS_TABLES_MANAGE}
              title="Printer configuration restricted"
              description="Your account does not currently have permission to configure section printers."
            >
              <PrinterConfigurationPage />
            </AccessControlledDashboardPage>
          }
        />
      </Route>

      <Route
        path="billing"
        element={
          <AccessControlledDashboardPage
            control={AccessControlCode.DASHBOARD_BILLINGS_ACCESS}
            title="Billing access restricted"
            description="Your account does not currently have permission to access billing."
          >
            <Billing />
          </AccessControlledDashboardPage>
        }
      />

      <Route path="ebm">
        <Route
          index
          element={<Navigate to="/settings/ebm/configuration" replace />}
        />
        <Route
          path="configuration"
          element={
            <AccessControlledDashboardPage
              control={AccessControlCode.SETTINGS_EBM_CONFIG_MANAGE}
              title="EBM configuration restricted"
              description="Your account does not currently have permission to configure the RRA fiscal device."
              outletCapability={{
                enabled: hasFiscalIntegration,
                title: 'RRA fiscal integration not available',
                description:
                  'This company is not registered under a country with RRA fiscal integration enabled.',
              }}
            >
              <EbmConfigurationPage />
            </AccessControlledDashboardPage>
          }
        />
        <Route
          path="missing-fiscal-setup"
          element={
            <AccessControlledDashboardPage
              control={AccessControlCode.SETTINGS_EBM_FISCAL_SETUP_MANAGE}
              title="Fiscal setup access restricted"
              description="Your account does not currently have permission to manage menu item fiscal setup."
              outletCapability={{
                enabled: hasFiscalIntegration,
                title: 'RRA fiscal integration not available',
                description:
                  'This company is not registered under a country with RRA fiscal integration enabled.',
              }}
            >
              <MenuItemsMissingFiscalSetupPage />
            </AccessControlledDashboardPage>
          }
        />
        <Route
          path="configured-fiscal-setup"
          element={
            <AccessControlledDashboardPage
              control={AccessControlCode.SETTINGS_EBM_FISCAL_SETUP_MANAGE}
              title="Fiscal setup access restricted"
              description="Your account does not currently have permission to manage menu item fiscal setup."
              outletCapability={{
                enabled: hasFiscalIntegration,
                title: 'RRA fiscal integration not available',
                description:
                  'This company is not registered under a country with RRA fiscal integration enabled.',
              }}
            >
              <MenuItemsConfiguredFiscalSetupPage />
            </AccessControlledDashboardPage>
          }
        />
        <Route
          path="sale-records"
          element={
            <AccessControlledDashboardPage
              control={AccessControlCode.SETTINGS_EBM_SALE_RECORDS_MANAGE}
              title="Sale records access restricted"
              description="Your account does not currently have permission to manage EBM sale records."
              outletCapability={{
                enabled: hasFiscalIntegration,
                title: 'RRA fiscal integration not available',
                description:
                  'This company is not registered under a country with RRA fiscal integration enabled.',
              }}
            >
              <EbmSaleRecordsPage />
            </AccessControlledDashboardPage>
          }
        />
      </Route>
    </Route>
  );
};
